import React, { useEffect, useState } from 'react';
import { Search, Users, MoreHorizontal, Filter, Briefcase, Plus, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { jobBoardApi } from '../../api/jobBoardApi';
import { JobDto } from '../../types/jobBoard';
import Spinner from '../../components/Spinner';

const JobsList = () => {
  const [jobs, setJobs] = useState<JobDto[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await jobBoardApi.getJobs();
        setJobs(response.data);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      <header className="mt-5 mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <Briefcase className="text-indigo-600" size={24} />
            All Job Postings
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Manage, edit, and monitor your current listings.
          </p>
        </div>
        <Link
          to="/job-board/jobs/create"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-lg shadow-indigo-100 active:scale-95"
        >
          <Plus size={20} />
          Post Job
        </Link>
      </header>

      <div className="flex flex-col md:flex-row gap-4 mb-10">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input 
            type="text" 
            placeholder="Search roles, locations, or tags..." 
            className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none shadow-sm transition-all text-slate-700 placeholder:text-slate-400"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="flex items-center gap-2 px-6 py-3.5 bg-white border border-slate-200 rounded-2xl text-slate-600 font-bold hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm">
          <Filter size={20} /> Filters
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center p-20">
          <Spinner size="h-10 w-10" />
          <p className="mt-4 text-slate-400 font-medium animate-pulse">Loading your postings...</p>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-6">
        {jobs.filter(j => j.jobTitle.toLowerCase().includes(search.toLowerCase())).length > 0 ? (
          jobs.filter(j => j.jobTitle.toLowerCase().includes(search.toLowerCase())).map((job) => (
            <div key={job.jobId} className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-xl hover:shadow-indigo-500/5 hover:border-indigo-100 transition-all duration-300 group">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Link to={`/job-board/jobs/${job.jobId}`} className="text-xl font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">
                    {job.jobTitle}
                  </Link>
                  {new Date(job.postedDate) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && (
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase rounded-lg tracking-widest">New</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500 font-medium">
                  <span className="flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-full border border-slate-100">
                    <Users size={16} className="text-indigo-500" /> 
                    {job.numberOfPeopleNeeded} {job.numberOfPeopleNeeded === 1 ? 'Vacancy' : 'Vacancies'}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar size={16} className="text-slate-400" /> 
                    Posted {new Date(job.postedDate).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between md:justify-end gap-6 pt-4 md:pt-0 border-t md:border-t-0 border-slate-50">
                <div className="flex flex-col items-center md:items-end">
                  <p className="text-2xl font-bold text-indigo-600">{job.applicants?.length || 0}</p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Applicants</p>
                </div>
                <div className="h-10 w-[1px] bg-slate-100 hidden md:block"></div>
                <div className="flex items-center gap-3">
                  <Link to={`/job-board/jobs/${job.jobId}`} className="px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-2xl text-sm font-bold hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all duration-200 shadow-sm">
                    Manage
                  </Link>
                  <button className="p-3 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-2xl transition-all">
                    <MoreHorizontal size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center p-20 bg-white rounded-[2.5rem] border border-dashed border-slate-200">
            <div className="p-4 bg-slate-50 rounded-3xl mb-4">
              <Briefcase size={40} className="text-slate-300" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No jobs found</h3>
            <p className="text-slate-500 text-sm text-center max-w-xs mt-1">Try adjusting your search or filters to find what you're looking for.</p>
            <button onClick={() => setSearch('')} className="mt-6 text-indigo-600 font-bold hover:underline">Clear all filters</button>
          </div>
        )}
      </div>
      )}
    </div>
  );
};
export default JobsList;